/**
 * MatterEconomicsWorkspace - the matter's money, behind ONE tab.
 *
 * Economics used to occupy three of the matter's top-level tabs: Time,
 * Economics and Billing Rates, sitting alongside Overview, Documents, Q&As,
 * Tasks and Notes. Three of eight tabs for one subject, and the subject reads
 * as one thing - you check the budget, then look at what was logged against
 * it, then at the rate that produced the number. Splitting that across the
 * top-level bar made the money the loudest thing in the matter and still made
 * you leave one view to answer a question the next one raised.
 *
 * So it is one tab that opens as a WORKSPACE with its own sub-tab strip:
 *
 *   Overview       budget / billed / WIP / remaining, the consumption bar,
 *                  charts and the by-person breakdown
 *   Time keeping   the time-entry table, filters, add / edit / delete
 *   Billing rates  standard vs this-matter override, per person
 *
 * This is a RE-HOUSING, not a rewrite. Each sub-tab is the existing component,
 * unchanged, with its own fetches and every action it already had (add time,
 * edit rates, create overrides, CSV export). Nothing about the data layer
 * moved.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiChartLineUpLight, PiClockLight, PiCurrencyDollarLight } from 'react-icons/pi';
import SubTabBar from '../../shared/TailwindComponents/SubTabBar';
import MatterEconomicsTab from './MatterEconomicsTab';
import MatterTimeTab from './MatterTimeTab';
import MatterBillingRatesTab from './MatterBillingRatesTab';
import type { MatterData } from '../MatterDetails';

type EconomicsView = 'overview' | 'time' | 'rates';

interface Props {
  matterData: MatterData | null;
  userAccessLevel: 'edit' | 'admin' | 'view';
  /** Apply a saved patch (pricing, mat-19) to the matter the page holds. */
  onUpdate?: (patch?: Record<string, any>) => void;
}

const MatterEconomicsWorkspace: React.FC<Props> = ({ matterData, userAccessLevel, onUpdate }) => {
  const { t } = useTranslation();
  const [view, setView] = useState<EconomicsView>('overview');

  const subTabs = useMemo(() => [
    {
      id: 'overview',
      label: t('Overview'),
      icon: <PiChartLineUpLight className="h-3 w-3" />,
    },
    {
      id: 'time',
      label: t('Time keeping'),
      icon: <PiClockLight className="h-3 w-3" />,
    },
    {
      id: 'rates',
      label: t('Billing rates'),
      icon: <PiCurrencyDollarLight className="h-3 w-3" />,
    },
  ], [t]);

  return (
    <div className="flex flex-col min-h-0">
      <div data-tour-id="economics-sub-tabs">
        <SubTabBar
          tabs={subTabs}
          activeTab={view}
          onTabChange={(id) => setView(id as EconomicsView)}
        />
      </div>

      <div className="flex-1 min-h-0">
        {/* Each sub-tab keeps its own state and fetches, so switching away and
            back re-reads rather than showing a stale figure. */}
        {view === 'overview' && (
          <MatterEconomicsTab
            matterData={matterData}
            userAccessLevel={userAccessLevel}
            onUpdate={onUpdate}
          />
        )}
        {view === 'time' && (
          <MatterTimeTab matterData={matterData} userAccessLevel={userAccessLevel} />
        )}
        {view === 'rates' && (
          <MatterBillingRatesTab matterData={matterData} userAccessLevel={userAccessLevel} />
        )}
      </div>
    </div>
  );
};

export default MatterEconomicsWorkspace;
